import { Component, ReactNode, useState, useEffect } from 'react';
import { HashRouter } from 'react-router-dom';
import { AppProviders } from '@app/providers/AppProviders';
import { AppRouter } from '@app/router/AppRouter';
import { CommandPalette } from '@shared/ui';

// Timestamps of recent auto-reloads (in-memory, resets on page load)
const reloadTimestamps: number[] = [];
const MAX_AUTO_RELOADS = 3;
const RELOAD_WINDOW_MS = 30_000;

function isChunkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = error.message ?? '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

function tryAutoReload(): boolean {
  const now = Date.now();
  // Drop entries outside the window
  while (reloadTimestamps.length && now - reloadTimestamps[0] > RELOAD_WINDOW_MS) {
    reloadTimestamps.shift();
  }
  if (reloadTimestamps.length < MAX_AUTO_RELOADS) {
    reloadTimestamps.push(now);
    window.location.reload();
    return true;
  }
  return false;
}

class ChunkErrorBoundary extends Component<{ children: ReactNode }, { crashed: boolean; isChunk: boolean }> {
  state = { crashed: false, isChunk: false };

  static getDerivedStateFromError(error: unknown) {
    return { crashed: true, isChunk: isChunkError(error) };
  }

  componentDidCatch(error: unknown) {
    if (isChunkError(error)) tryAutoReload();
  }

  render() {
    const { crashed, isChunk } = this.state;
    if (!crashed) return this.props.children;
    // Chunk errors: auto-reload in progress — show nothing (flash is brief)
    if (isChunk && reloadTimestamps.length < MAX_AUTO_RELOADS) return null;
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, background: '#081019', color: '#f1f5f9' }}>
        <p style={{ fontSize: 14, color: '#94a3b8' }}>
          {isChunk ? 'Nouvelle version disponible.' : 'Une erreur est survenue lors du chargement.'}
        </p>
        {!isChunk && (
          <button
            onClick={() => {
              window.location.hash = '/';
              this.setState({ crashed: false, isChunk: false });
            }}
            style={{ padding: '10px 24px', borderRadius: 99, background: 'transparent', color: '#94a3b8', border: '1px solid #334155', fontSize: 14, cursor: 'pointer' }}
          >
            ← Retour à l'accueil
          </button>
        )}
        <button
          onClick={() => { reloadTimestamps.length = 0; window.location.reload(); }}
          style={{ padding: '10px 24px', borderRadius: 99, background: '#10b981', color: '#fff', border: 'none', fontSize: 14, cursor: 'pointer' }}
        >
          Recharger
        </button>
      </div>
    );
  }
}

function AppInner() {
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      <AppProviders>
        <AppRouter />
      </AppProviders>
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </>
  );
}

function App() {
  return (
    <ChunkErrorBoundary>
      <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppInner />
      </HashRouter>
    </ChunkErrorBoundary>
  );
}

export default App;
